import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import BrandLink from '@/components/ui/BrandLink';
import Tagline from '@/components/ui/Tagline';
import CookieSettingsLink from '@/components/cookies/CookieSettingsLink';
import UgcFacebookReel from '@/components/marketing/UgcFacebookReel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { createPageUrl } from '@/utils';

import dashboardShot from '@/assets/landing/screens/dashboard.webp';
import enveloppesShot from '@/assets/landing/screens/enveloppes.webp';
import remunerationShot from '@/assets/landing/screens/remuneration.webp';

import {
  ArrowRight,
  Bell,
  Calculator,
  Check,
  Crown,
  CreditCard,
  FileText,
  PiggyBank,
  Shield,
  Sparkles,
  TrendingUp,
  Zap,
  Menu,
  X,
} from 'lucide-react';

// PERF: keep Tarifs bundle light (tracking module is loaded only on interaction).
import { lazySendMetaEvent as sendMetaEvent, lazyTrackEvent as trackEvent } from '@/lib/lazyTracking';

function formatPriceFr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n);
}

const TRIAL_FLOW_URL = `/register?next=${encodeURIComponent('/onboarding?next=/billing')}`;

const FACEBOOK_PAGE_URL = 'https://www.facebook.com/profile.php?id=61585414149400';

function FacebookIcon({ className = 'w-5 h-5' }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
    >
      <path d="M22 12.06C22 6.51 17.52 2 11.96 2S2 6.51 2 12.06C2 17.08 5.66 21.23 10.44 22v-7.03H7.9v-2.91h2.54V9.85c0-2.5 1.49-3.88 3.77-3.88 1.09 0 2.23.2 2.23.2v2.46h-1.25c-1.23 0-1.61.76-1.61 1.54v1.85h2.74l-.44 2.91h-2.3V22C18.34 21.23 22 17.08 22 12.06z" />
    </svg>
  );
}

export default function Tarifs() {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const yearlyDiscount = useMemo(() => {
    // Plus: 6.99 * 12 vs 69
    try {
      return Math.max(0, Math.round((1 - (69 / (6.99 * 12))) * 100));
    } catch {
      return 0;
    }
  }, []);

  const plans = useMemo(
    () => [
      {
        id: 'free',
        name: 'Starter',
        icon: Zap,
        price: { monthly: 0, yearly: 0 },
        tagline: 'Pour découvrir TrésoPilot et adopter le réflexe “vrai net”',
        highlight: 'Découverte',
        accent: 'slate',
        audience: 'Vous voulez tester la logique TrésoPilot avant de passer à une formule payante.',
        bullets: [
          'Dashboard clair : CA encaissé (TTC si TVA)',
          'Provisions automatiques URSSAF + impôts (+ TVA si applicable)',
          'Échéancier (URSSAF, impôt, CFE, TVA si applicable)',
          'Objectifs + suivi du “vrai net”',
          'Factures : 3 factures de test (à vie)',
          '1 activité · Historique 3 mois',
        ],
        limits: [
          'Pas d’enveloppes virtuelles',
          'Pas d’exports PDF/CSV',
          'Pas de rappels email',
          'Pas de livre des recettes',
        ],
        cta: 'Commencer gratuitement',
        ctaVariant: 'outline',
      },
      {
        id: 'plus',
        name: 'Plus',
        icon: Sparkles,
        price: { monthly: 6.99, yearly: 69 },
        tagline: 'Le meilleur ratio “charge mentale → tranquillité”',
        highlight: 'Le plus choisi',
        accent: 'emerald',
        audience: 'Vous voulez enfin provisionner, suivre vos enveloppes et arrêter de gérer au feeling.',
        bullets: [
          'Tout du Starter',
          'Enveloppes virtuelles (TVA, URSSAF, impôts, salaire)',
          'Rappels in‑app + emails',
          'Exports PDF/CSV (banque, compta)',
          'Livre des recettes (registre encaissements)',
          'Factures/Devis/Clients : 10 factures / mois',
          'Devis acceptés en ligne (email + code + preuve)',
          'Contrats : 5 / mois (acceptation incluse)',
          'Historique illimité · Support prioritaire',
        ],
        limits: ['Pas de multi‑activités', 'Pas de rapports & IA premium'],
        cta: 'Choisir Plus',
        ctaVariant: 'default',
      },
      {
        id: 'pro',
        name: 'Pro',
        icon: Crown,
        price: { monthly: 12.99, yearly: 129 },
        tagline: 'Illimité + prévisionnel + rémunération + IA',
        highlight: 'Illimité',
        accent: 'purple',
        audience: 'Vous voulez centraliser votre pilotage, vos documents et vos décisions dans un seul outil.',
        bullets: [
          'Tout de Plus',
          'Multi‑activités illimité',
          'Factures + devis + clients illimités',
          'Acceptation numérique : devis + contrats (preuve envoyée aux 2 parties)',
          'Factur‑X prêt (export illimité) + PDP bientôt',
          'Prévisionnel de trésorerie',
          'Alertes seuils TVA',
          'Simulateur salaire / rémunération',
          'Rapports & recommandations IA',
        ],
        limits: [],
        cta: 'Choisir Pro',
        ctaVariant: 'default',
      },
    ],
    []
  );

  const goRegister = (intent) => {
    // Optional: keep intent for later (Billing page / onboarding can use it in the future)
    try {
      if (typeof window !== 'undefined' && intent) {
        window.localStorage.setItem('tresopilot_pricing_intent', JSON.stringify(intent));
      }
    } catch (_) {}
    navigate(TRIAL_FLOW_URL);
  };


  // Track CTA clicks like on Landing (first-party + optional Meta CAPI Lead).
  const handlePricingCta = async ({ label, placement, intent }) => {
    const page = typeof window !== 'undefined' ? window.location.href : '';
    const nextPath = TRIAL_FLOW_URL;

    try {
      await trackEvent({
        eventName: 'pricing_cta_click',
        page,
        source: 'pricing',
        customData: {
          label,
          placement,
          nextPath,
          billingPeriod,
          ...(intent || {}),
        },
      });
    } catch (_) {}

    try {
      void sendMetaEvent({
        eventName: 'Lead',
        eventSourceUrl: page,
        customData: { label, placement, nextPath, billingPeriod, source: 'pricing', ...(intent || {}) },
      });
    } catch (_) {}

    goRegister(intent);
  };



  const selectedPrice = (plan) => {
    const price = billingPeriod === 'yearly' ? plan.price.yearly : plan.price.monthly;
    if (!price) return '0';
    return formatPriceFr(price);
  };

  const yearlyEquivalentMonthly = (plan) => {
    if (!plan?.price?.yearly) return null;
    return formatPriceFr(plan.price.yearly / 12);
  };

  const scrollToPricing = () => {
    if (typeof document === 'undefined') return;
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const go = (path) => navigate(path);

  const handleSocialClick = ({ platform, placement }) => {
    try {
      void trackEvent({
        eventName: 'pricing_social_click',
        page: typeof window !== 'undefined' ? window.location.href : '',
        source: 'pricing',
        customData: { platform, placement, url: FACEBOOK_PAGE_URL },
      });
    } catch (_) {}
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <BrandLink to="/" />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Button variant="ghost" onClick={() => go('/')}>Accueil</Button>
            <Button variant="ghost" onClick={() => go(createPageUrl('FAQ'))}>FAQ</Button>
            <Button
              variant="ghost"
              onClick={() => go(`${createPageUrl('Login')}?next=${encodeURIComponent(createPageUrl('Dashboard'))}`)}
              data-track="cta"
              data-cta="Connexion"
              data-placement="header"
            >
              Connexion
            </Button>

            <Button
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              data-track="cta"
              data-cta="Démarrer l’essai Pro"
              data-placement="header"
              onClick={() => handlePricingCta({ label: 'Démarrer l’essai Pro', placement: 'header', intent: { plan: 'pro', period: 'trial' } })}
            >
              Démarrer l’essai Pro
            </Button>

            <a
              href={FACEBOOK_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook TrésoPilot"
              onClick={() => handleSocialClick({ platform: 'facebook', placement: 'header' })}
              className="inline-flex items-center justify-center w-10 h-10 rounded-md border border-slate-200 text-slate-600 hover:text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50 transition-colors"
            >
              <FacebookIcon className="w-5 h-5" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Ouvrir le menu"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </Button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/50 z-50 md:hidden"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-[92vw] max-w-sm bg-white z-50 shadow-2xl md:hidden flex flex-col max-h-[100svh] overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <BrandLink to="/" />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Fermer le menu"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>

              <nav className="p-6 space-y-4 flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y">
                <a
                  href="/"
                  onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); go('/'); }}
                  className="block text-lg text-slate-700 hover:text-emerald-600 transition-colors"
                >
                  Accueil
                </a>
                <a
                  href={createPageUrl('FAQ')}
                  onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); go(createPageUrl('FAQ')); }}
                  className="block text-lg text-slate-700 hover:text-emerald-600 transition-colors"
                >
                  FAQ
                </a>
                <a
                  href={createPageUrl('Contact')}
                  onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); go(createPageUrl('Contact')); }}
                  className="block text-lg text-slate-700 hover:text-emerald-600 transition-colors"
                >
                  Contact
                </a>

                <div className="pt-4 space-y-3">
                  <Button
                    variant="outline"
                    className="w-full"
                    data-track="cta"
                    data-cta="Connexion"
                    data-placement="mobile_menu"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      go(`${createPageUrl('Login')}?next=${encodeURIComponent(createPageUrl('Dashboard'))}`);
                    }}
                  >
                    Connexion
                  </Button>
                  <Button
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    data-track="cta"
                    data-cta="Démarrer l’essai Pro"
                    data-placement="mobile_menu"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handlePricingCta({ label: 'Démarrer l’essai Pro', placement: 'mobile_menu', intent: { plan: 'pro', period: 'trial' } });
                    }}
                  >
                    Démarrer l’essai Pro
                  </Button>

                  <a
                    href={FACEBOOK_PAGE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleSocialClick({ platform: 'facebook', placement: 'mobile_menu' })}
                    className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors pt-2"
                  >
                    <FacebookIcon className="w-5 h-5" />
                    <span>Facebook TrésoPilot</span>
                  </a>
                </div>

                <div className="pt-6 border-t border-slate-100">
                  <div className="text-sm text-slate-500">En continuant, vous acceptez nos cookies.</div>
                  <div className="mt-2">
                    <CookieSettingsLink className="text-sm text-emerald-600 hover:text-emerald-700" />
                  </div>
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-32 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Micro‑entrepreneurs & indépendants</Badge>
              <Badge className="bg-slate-100 text-slate-700 border-slate-200">Paiement sécurisé Stripe</Badge>
              <Badge className="bg-slate-100 text-slate-700 border-slate-200">Annulation à tout moment</Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
              L’offre qui vous aide à piloter votre activité
              <span className="text-emerald-600"> avec plus de clarté et moins de stress</span>.
            </h1>
            <p className="text-lg text-slate-600 mt-4 leading-relaxed">
              TrésoPilot transforme votre chiffre d’affaires encaissé (TTC si TVA) en <strong>provisions</strong>, <strong>échéances</strong> et <strong>vrai net</strong>.
              Vous savez ce qu’il faut mettre de côté, ce que vous pouvez vous verser et quelle formule choisir pour avancer plus sereinement.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                onClick={() => handlePricingCta({ label: 'Essai Pro 7 jours', placement: 'hero_primary', intent: { plan: 'pro', period: 'trial' } })}
              >
                Essai Pro 7 jours <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={scrollToPricing}>
                Comparer les offres
              </Button>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {[
                'Vous gardez la main sur vos choix et vos données.',
                'Aucune connexion bancaire requise.',
                'Plus et Pro enlèvent une vraie charge mentale au quotidien.',
                'Le plan gratuit permet de découvrir la logique avant de monter en gamme.',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-slate-100">
                  <div className="bg-emerald-50 rounded-xl p-2 mt-0.5">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-tr from-emerald-200/40 to-purple-200/40 blur-2xl rounded-3xl" />
            <div className="relative bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-xl">
              <div className="p-5 border-b border-slate-100 bg-slate-50/80">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-2xl bg-white p-4 border border-slate-100">
                    <div className="flex items-center gap-2 text-slate-900 font-semibold">
                      <Calculator className="w-4 h-4 text-emerald-600" />
                      Provisions
                    </div>
                    <div className="text-sm text-slate-600 mt-1">URSSAF, impôts, TVA… visibles plus tôt.</div>
                  </div>
                  <div className="rounded-2xl bg-white p-4 border border-slate-100">
                    <div className="flex items-center gap-2 text-slate-900 font-semibold">
                      <Bell className="w-4 h-4 text-emerald-600" />
                      Échéances
                    </div>
                    <div className="text-sm text-slate-600 mt-1">Vous anticipez au lieu de subir.</div>
                  </div>
                  <div className="rounded-2xl bg-white p-4 border border-slate-100">
                    <div className="flex items-center gap-2 text-slate-900 font-semibold">
                      <PiggyBank className="w-4 h-4 text-emerald-600" />
                      Vrai net
                    </div>
                    <div className="text-sm text-slate-600 mt-1">Vous voyez ce qui est vraiment à vous.</div>
                  </div>
                </div>
              </div>
              <img src={dashboardShot} alt="Aperçu du dashboard TrésoPilot" className="w-full h-auto" loading="lazy" />
              <div className="p-5 border-t border-slate-100">
                <div className="flex items-center gap-2 text-slate-700">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">
                    Vous pilotez sur le <strong>CA encaissé</strong>, les <strong>provisions</strong> et la <strong>trésorerie disponible</strong> — pas sur une impression.
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="rounded-3xl border-slate-100">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="bg-slate-100 rounded-2xl p-3">
                  <CreditCard className="w-6 h-6 text-slate-700" />
                </div>
                <div>
                  <CardTitle>Votre banque affiche un solde</CardTitle>
                  <CardDescription>…mais pas votre “argent vraiment dispo”.</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="text-slate-600">
              URSSAF, impôts, TVA et CFE ne sont pas “déjà réservés”. TrésoPilot les transforme en montants à mettre de côté, automatiquement.
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-slate-100">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="bg-emerald-50 rounded-2xl p-3">
                  <Calculator className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <CardTitle>Vous saisissez votre CA TTC</CardTitle>
                  <CardDescription>L’app calcule la suite selon votre profil.</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="text-slate-600">
              TVA, ACRE, versement libératoire… TrésoPilot déduit ce qui doit être provisionné selon vos paramètres (et affiche un vrai net lisible).
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-slate-100">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="bg-purple-50 rounded-2xl p-3">
                  <PiggyBank className="w-6 h-6 text-purple-700" />
                </div>
                <div>
                  <CardTitle>Vous savez combien vous verser</CardTitle>
                  <CardDescription>…sans grignoter votre trésorerie.</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="text-slate-600">
              En Plus/Pro, vos enveloppes (URSSAF/TVA/impôts/salaire) vous guident pour lisser et vous rémunérer de façon durable.
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
            <img src={enveloppesShot} alt="Enveloppes virtuelles" className="w-full h-auto" loading="lazy" />
            <div className="p-5">
              <div className="font-semibold text-slate-900">Enveloppes virtuelles</div>
              <div className="text-sm text-slate-600 mt-1">Un virement hebdo simple au lieu d’un stress trimestriel.</div>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
            <img src={remunerationShot} alt="Rémunération" className="w-full h-auto" loading="lazy" />
            <div className="p-5">
              <div className="font-semibold text-slate-900">Rémunération</div>
              <div className="text-sm text-slate-600 mt-1">Suivez ce que vous vous versez, et gardez un cap.</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 text-white border border-slate-700">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-6 h-6 text-emerald-300" />
              <div className="font-semibold">Paiement sécurisé</div>
            </div>
            <div className="text-sm text-slate-200 leading-relaxed">
              Les paiements sont gérés par Stripe. Vous pouvez annuler quand vous voulez.
              <div className="mt-3 flex items-center gap-2 text-slate-200">
                <FileText className="w-4 h-4" />
                <span>Factures/devis propres (Plus/Pro) + Factur‑X (Pro).</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick choice */}
      <section className="max-w-6xl mx-auto px-4 pb-4">
        <div className="rounded-3xl border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 mb-3">
                <Sparkles className="w-4 h-4" />
                Choisissez en 30 secondes
              </div>
              <h3 className="text-2xl font-semibold text-slate-900">Quelle formule est la plus adaptée à votre façon de gérer ?</h3>
              <p className="text-slate-600 mt-2">
                Commencez gratuitement pour découvrir, choisissez Plus pour alléger la charge mentale, ou Pro pour centraliser votre pilotage et aller plus loin.
              </p>
            </div>
            <Button variant="outline" onClick={scrollToPricing}>
              Voir les offres
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan) => {
              const Icon = plan.icon;
              const accentClasses =
                plan.accent === 'emerald'
                  ? 'border-emerald-200 bg-emerald-50/60'
                  : plan.accent === 'purple'
                    ? 'border-purple-200 bg-purple-50/60'
                    : 'border-slate-200 bg-slate-50';

              return (
                <div key={`decision-${plan.id}`} className={`rounded-3xl border p-5 ${accentClasses}`}>
                  <div className="flex items-center gap-2 text-slate-900 font-semibold">
                    <Icon className="w-5 h-5" />
                    {plan.name}
                  </div>
                  <div className="text-sm text-slate-600 mt-3 min-h-[72px]">{plan.audience}</div>
                  <div className="mt-4 text-sm text-slate-700">
                    <span className="font-semibold">À partir de :</span>{' '}
                    {plan.id === 'free' ? '0€' : `${formatPriceFr(plan.price.monthly)}€/mois`}
                  </div>
                  <Button
                    className={`mt-4 w-full ${
                      plan.id === 'plus'
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                        : plan.id === 'pro'
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
                          : ''
                    }`}
                    variant={plan.ctaVariant}
                    onClick={() =>
                      plan.id === 'pro'
                        ? handlePricingCta({ label: 'Essai Pro 7 jours', placement: 'decision_helper', intent: { plan: 'pro', period: 'trial' } })
                        : handlePricingCta({ label: plan.cta, placement: 'decision_helper', intent: { plan: plan.id, period: billingPeriod } })
                    }
                  >
                    {plan.id === 'pro' ? 'Essai Pro 7 jours' : plan.cta}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Tarifs & Offres</h2>
          <p className="text-slate-600 mt-2">Choisissez la formule qui enlève le plus de charge mentale à votre activité.</p>
          <p className="text-sm text-slate-500 mt-3">Vous pouvez commencer gratuitement, ou passer directement à Plus / Pro si vous voulez gagner du temps dès maintenant.</p>
        </div>

        <div className="flex flex-col items-center justify-center gap-4 mb-6">
          <Tabs value={billingPeriod} onValueChange={setBillingPeriod}>
            <TabsList className="bg-slate-100">
              <TabsTrigger value="monthly">Mensuel</TabsTrigger>
              <TabsTrigger value="yearly" className="relative">
                Annuel
                {yearlyDiscount > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[10px] px-1.5">
                    -{yearlyDiscount}%
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-slate-500">
            <span className="rounded-full bg-slate-100 px-3 py-1">Paiement sécurisé Stripe</span>
            <span className="rounded-full bg-slate-100 px-3 py-1">Annulation à tout moment</span>
            <span className="rounded-full bg-slate-100 px-3 py-1">Sans connexion bancaire</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan, idx) => {
            const Icon = plan.icon;
            const price = selectedPrice(plan);
            const isYearly = billingPeriod === 'yearly';
            const per = plan.id === 'free' ? '' : isYearly ? '/an' : '/mois';
            const accent = plan.accent;
            const border =
              accent === 'emerald'
                ? 'border-emerald-500 shadow-lg shadow-emerald-500/10'
                : accent === 'purple'
                  ? 'border-purple-400/60 shadow-lg shadow-purple-500/10'
                  : 'border-slate-100';

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * idx }}
                className={`relative bg-white rounded-3xl border-2 p-6 flex flex-col ${border}`}
              >
                {plan.highlight && (
                  <Badge
                    className={`absolute -top-3 left-1/2 -translate-x-1/2 border-0 px-4 py-1 font-bold shadow ${
                      accent === 'emerald'
                        ? 'bg-emerald-500 text-white'
                        : accent === 'purple'
                          ? 'bg-purple-600 text-white'
                          : 'bg-slate-700 text-white'
                    }`}
                  >
                    {plan.highlight}
                  </Badge>
                )}

                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Icon className="w-6 h-6 text-slate-700" />
                      <h3 className="text-2xl font-bold text-slate-900">{plan.name}</h3>
                    </div>
                    <div className="text-slate-600 mt-1">{plan.tagline}</div>
                    <div className="mt-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700 border border-slate-100">
                      <span className="font-semibold text-slate-900">Idéal si :</span> {plan.audience}
                    </div>
                  </div>
                </div>

                <div className="mb-5">
                  <div className="flex items-end gap-1">
                    <span className="text-5xl font-bold text-slate-900">{price}€</span>
                    <span className="text-slate-500 pb-1">{per}</span>
                  </div>
                  {plan.id !== 'free' && (
                    <div className="text-xs text-slate-500 mt-1 space-y-1">
                      <div>{isYearly ? 'Facturation annuelle (économie vs mensuel)' : 'Annulez quand vous voulez.'}</div>
                      {isYearly && <div>Soit environ {yearlyEquivalentMonthly(plan)}€ / mois</div>}
                    </div>
                  )}
                </div>

                <div className="space-y-2 text-sm mb-5">
                  {plan.bullets.map((b) => (
                    <div key={b} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{b}</span>
                    </div>
                  ))}
                </div>

                {plan.limits?.length ? (
                  <div className="mt-auto">
                    <div className="text-xs font-semibold text-slate-500 mb-2">Limites</div>
                    <div className="space-y-1 text-xs text-slate-500">
                      {plan.limits.map((l) => (
                        <div key={l} className="flex items-start gap-2">
                          <span className="mt-0.5">•</span>
                          <span>{l}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-auto" />
                )}

                <Button
                  className={`mt-6 w-full ${
                    plan.id === 'plus'
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                      : plan.id === 'pro'
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
                        : ''
                  }`}
                  variant={plan.ctaVariant}
                  onClick={() => handlePricingCta({ label: plan.cta, placement: 'plan_card', intent: { plan: plan.id, period: billingPeriod } })}
                >
                  {plan.cta}
                </Button>
              </motion.div>
            );
          })}
        </div>

        {/* Pack 60 jours */}
        <div className="mt-8 relative">
          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-900 text-white border-0 font-bold text-sm px-4 py-1 shadow-lg z-10">
            Alternative sans abonnement
          </Badge>
          <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl">
            <div className="absolute inset-0 opacity-10">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                  backgroundSize: '32px 32px',
                }}
              />
            </div>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-2">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-10 h-10" />
                  <h3 className="text-2xl md:text-3xl font-bold">Pack Tranquillité 60 jours</h3>
                </div>
                <p className="text-purple-100 text-lg">
                  Vous préférez tester sans abonnement ? Prenez <strong>60 jours</strong> d’accès aux fonctionnalités Plus avec un <strong>paiement unique</strong>.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                  {[
                    '10 factures / 60 jours (quota global)',
                    'Devis illimités',
                    'Contrats : 3 / 60 jours (acceptation incluse)',
                    'Acceptation numérique : preuve envoyée aux 2 parties',
                    'Clients illimités',
                    'Enveloppes virtuelles',
                    'Rappels email',
                    'Exports PDF & CSV',
                    'Livre des recettes',
                  ].map((t) => (
                    <div key={t} className="flex items-center gap-3 text-purple-100">
                      <div className="bg-white/20 rounded-lg p-2">
                        <Check className="w-5 h-5" />
                      </div>
                      <span>{t}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 text-sm text-purple-100">
                  <strong>Conditions :</strong> offre réservée aux nouveaux comptes (1 seule fois par compte). Aucun renouvellement automatique.
                </div>
              </div>

              <div className="text-center lg:text-right">
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 border border-white/20 mb-4">
                  <div className="text-6xl font-bold mb-1">9,99€</div>
                  <div className="text-purple-200">Paiement unique</div>
                  <div className="text-sm text-purple-300 mt-2">Moins de 5€/mois sur 2 mois</div>
                </div>
                <Button
                  size="lg"
                  className="w-full bg-white text-purple-700 hover:bg-purple-50 h-14 text-lg font-bold shadow-xl"
                  onClick={() => handlePricingCta({ label: 'Obtenir le Pack', placement: 'pack_card', intent: { plan: 'pack', period: 'one_time' } })}
                >
                  <Shield className="w-5 h-5 mr-2" />
                  Tester 60 jours
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ mini */}
      <section className="max-w-4xl mx-auto px-4 pb-14">
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Questions fréquentes sur les tarifs</h2>
          <p className="text-slate-600 mt-2">Pas de piège : vous gardez la main.</p>
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 p-2">
          <Accordion type="single" collapsible>
            <AccordionItem value="item-1">
              <AccordionTrigger>Je peux annuler quand je veux ?</AccordionTrigger>
              <AccordionContent>
                Oui. L’abonnement mensuel ou annuel est annulable à tout moment via le portail de paiement. Vous gardez l’accès jusqu’à la fin de la période payée.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>Le Pack 60 jours se renouvelle ?</AccordionTrigger>
              <AccordionContent>
                Non. C’est un paiement unique : aucune reconduction automatique. Idéal si vous voulez tester sans abonnement.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>Comment ça m’aide face à URSSAF / TVA ?</AccordionTrigger>
              <AccordionContent>
                Vous saisissez votre CA encaissé (TTC si TVA). TrésoPilot calcule des provisions et vous affiche les échéances, pour éviter les surprises.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4">
              <AccordionTrigger>Le paiement est sécurisé ?</AccordionTrigger>
              <AccordionContent>
                Oui. Les paiements sont gérés par Stripe. Vos données bancaires ne transitent pas par nos serveurs.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5">
              <AccordionTrigger>Je commence par quoi ?</AccordionTrigger>
              <AccordionContent>
                Démarrez en Starter gratuit, ou prenez l’essai Pro 7 jours si vous voulez tout tester tout de suite.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            size="lg"
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            onClick={() => handlePricingCta({ label: 'Démarrer maintenant', placement: 'bottom_cta', intent: { plan: 'pro', period: 'trial' } })}
          >
            Démarrer maintenant <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button size="lg" variant="outline" onClick={() => navigate(createPageUrl('Contact'))}>
            Une question ? Contact
          </Button>
        </div>
      </section>

      {/* UGC video */}
      <section className="max-w-6xl mx-auto px-4 pb-12">
        <div className="rounded-3xl border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 bg-purple-50 border border-purple-100 rounded-full px-3 py-1 mb-4">
                <Sparkles className="w-4 h-4" />
                Retour d’expérience
              </div>
              <h3 className="text-2xl font-semibold text-slate-900">Une minute pour voir comment TrésoPilot peut simplifier la gestion</h3>
              <p className="text-slate-600 mt-2">
                Regardez ce qui a aidé une ambassadrice à mieux suivre ses provisions, ses échéances et ses documents, puis choisissez la formule qui vous correspond.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <Button onClick={() => handlePricingCta({ label: 'Essai Pro 7 jours', placement: 'ugc_video', intent: { plan: 'pro', period: 'trial' } })}>
                  Essai Pro 7 jours <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" onClick={scrollToPricing}>
                  Revoir les offres
                </Button>
              </div>
            </div>

            <div className="flex justify-center lg:justify-end">
              <UgcFacebookReel />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center gap-6 mb-8">
            <BrandLink to="/" variant="light" />

            <div className="flex items-center justify-center gap-3">
              <a
                href={FACEBOOK_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook TrésoPilot"
                onClick={() => handleSocialClick({ platform: 'facebook', placement: 'footer' })}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white transition-colors"
              >
                <FacebookIcon className="w-5 h-5" />
              </a>
              <span className="text-slate-300 text-sm">Rejoindre la communauté TrésoPilot sur Facebook</span>
            </div>

            {/* Contact Section */}
            <div className="bg-slate-800 rounded-xl p-4 max-w-md">
              <p className="text-slate-300 font-semibold mb-3 text-center">Contact</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-slate-400">Email :</span>
                  <a href="mailto:contact@tresopilot.fr" className="text-emerald-400 hover:text-emerald-300">
                    contact@tresopilot.fr
                  </a>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-slate-400">WhatsApp :</span>
                  <a href="https://wa.me/33756901580" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300">
                    +33 7 56 90 15 80
                  </a>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-3 text-sm">
              <a href={createPageUrl('FAQ')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('FAQ')); }}>
                FAQ
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('Contact')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('Contact')); }}>
                Contact
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('MentionsLegales')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('MentionsLegales')); }}>
                Mentions légales
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('CGU')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('CGU')); }}>
                CGU
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('Confidentialite')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('Confidentialite')); }}>
                Politique de confidentialité
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('Cookies')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('Cookies')); }}>
                Cookies
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('Disclaimer')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('Disclaimer')); }}>
                Avertissement
              </a>
              <span className="text-slate-600">•</span>
              <CookieSettingsLink />
            </div>
            <div className="bg-slate-800 rounded-xl p-4 max-w-3xl text-center">
              <p className="text-slate-400 text-sm leading-relaxed">
                <strong className="text-slate-300">Avertissement important :</strong> TrésoPilot n'est pas un service de comptabilité ou d'expertise-comptable. 
                Les montants, dates et estimations sont fournis à titre indicatif selon les informations que vous renseignez. 
                Vérifiez toujours vos obligations fiscales et sociales sur les portails officiels (URSSAF, impots.gouv.fr).
              </p>
            </div>
            <p className="text-emerald-200 text-sm">
              <Tagline className="text-emerald-200" />
            </p>
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} TrésoPilot. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
